import React from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
} from 'recharts';
import { Box, Typography, useTheme, useMediaQuery } from '@mui/material';

const GREEK_MONTHS = {
  Jan: 'Ιαν', Feb: 'Φεβ', Mar: 'Μαρ', Apr: 'Απρ', May: 'Μαϊ', Jun: 'Ιουν',
  Jul: 'Ιουλ', Aug: 'Αυγ', Sep: 'Σεπ', Oct: 'Οκτ', Nov: 'Νοε', Dec: 'Δεκ',
};

const formatLabel = (label, compact = false) => {
  if (!label) return '';
  const parts = label.split(' ');
  if (parts.length >= 2 && GREEK_MONTHS[parts[0]]) {
    const year = compact && parts[1].length === 4 ? parts[1].slice(2) : parts[1];
    return `${GREEK_MONTHS[parts[0]]} ${year}`;
  }
  return label;
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('el-GR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);

const LEGEND_LABELS = {
  total: 'Εισπράξεις',
  services_value: 'Τιμολόγηση (αξία υπηρεσιών)',
  subscription: 'Συνδρομές',
  one_time: 'Εφάπαξ',
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        boxShadow: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
        {formatLabel(label)}
      </Typography>
      <Typography variant="body2" sx={{ color: 'warning.main' }}>
        {LEGEND_LABELS.services_value}: {formatCurrency(row?.services_value)}
      </Typography>
      {(row?.services_vat > 0) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
          → από εκ των οποίων ΦΠΑ: {formatCurrency(row?.services_vat)}
        </Typography>
      )}
      <Typography variant="body2" sx={{ color: 'primary.main', mt: 0.5 }}>
        {LEGEND_LABELS.total}: {formatCurrency(row?.total)}
      </Typography>
      {(row?.subscription > 0 || row?.one_time > 0) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 1 }}>
          → Από συνδρομές: {formatCurrency(row?.subscription)} · Από εφάπαξ: {formatCurrency(row?.one_time)}
        </Typography>
      )}
    </Box>
  );
};

const RevenueActivityChart = ({ data, loading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const chartHeight = isMobile ? 260 : 320;
  const chartData = (data?.data || []).map((d) => ({
    ...d,
    name: d.label,
  }));

  if (loading) {
    return (
      <Box sx={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Φόρτωση...</Typography>
      </Box>
    );
  }

  if (!chartData.length) {
    return (
      <Box sx={{ height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">Δεν υπάρχουν δεδομένα για την επιλεγμένη περίοδο</Typography>
      </Box>
    );
  }

  const tickInterval = isMobile && chartData.length > 8 ? Math.ceil(chartData.length / 6) - 1 : 0;

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <ResponsiveContainer width="100%" height={chartHeight}>
        <ComposedChart
          data={chartData}
          margin={{ top: 8, right: isMobile ? 4 : 20, left: isMobile ? -12 : 0, bottom: isMobile ? 8 : 0 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="name"
            tickFormatter={(label) => formatLabel(label, isMobile)}
            tick={{ fontSize: isMobile ? 10 : 12 }}
            interval={tickInterval}
            angle={isMobile ? -40 : 0}
            textAnchor={isMobile ? 'end' : 'middle'}
            height={isMobile ? 48 : 30}
            stroke={theme.palette.text.secondary}
          />
          <YAxis
            tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)}
            tick={{ fontSize: isMobile ? 10 : 12 }}
            width={isMobile ? 36 : 48}
            stroke={theme.palette.text.secondary}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            formatter={(value) => LEGEND_LABELS[value] || value}
            wrapperStyle={{ fontSize: isMobile ? 12 : 14 }}
          />
          <Bar
            dataKey="services_value"
            fill={theme.palette.warning.main}
            name="services_value"
            radius={[2, 2, 0, 0]}
          />
          <Bar
            dataKey="total"
            fill={theme.palette.primary.main}
            name="total"
            radius={[2, 2, 0, 0]}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default RevenueActivityChart;
